/**
 * LandingProjects organism — renders a grid of the user's recent projects
 * (owned + shared-with-me) and an optional "New project" card.
 */

import { ProjectCard } from "@/components/molecules/projects/ProjectCard";
import { NewProjectCard } from "@/components/molecules/projects/NewProjectCard";
import { PRODUCT_STEPS } from "@/constants/steps";

export interface LandingProjectListItem {
  id: string;
  name: string;
  updatedAt: Date | string;
  ownerId: string;
  // Date on the server, string once it round-trips through SSR serialization.
  archivedAt: Date | string | null;
  deletedAt: Date | string | null;
  // Purge markers (LEV-2016), selected by `projects.list`. `purgeRequestedAt`
  // is the founder asking to skip the rest of the restore window;
  // `purgeStartedAt` is the purge job's claim, taken before its first
  // irreversible delete. Either one means the project can no longer be
  // restored, so the card stops offering it (see `isPurgePending`).
  purgeRequestedAt: Date | string | null;
  purgeStartedAt: Date | string | null;
  owner: {
    id: string;
    name: string | null;
    email: string | null;
    imageUrl: string | null;
  };
  blocks: Array<{ stepIndex: number }>;
  _count: { collaborators: number };
  // Name only: `projects.list` never sends collaborator emails to the client.
  collaborators: Array<{ user: { name: string | null } }>;
}

export interface LandingProjectsProps {
  projects: Array<LandingProjectListItem>;
  /** Lev app user id of the viewer (the same id stored on `Project.ownerId`).
   *  Compared against each project's `ownerId` to mark cards "You" vs the
   *  inviter's name. Note: for non-migrated users this equals the Clerk
   *  user id, but callers should source it from `clerkAuth().appUserId`
   *  (or RSC equivalent) — never the raw Clerk id. */
  viewerUserId?: string;
  userInitials?: string;
  /** When true, show the dashed "New project" card. */
  showNewProjectCard?: boolean;
  /** Called when the New project card is clicked. */
  onNewProjectClick?: () => void;
  /** When provided, project cards show delete option and this is called with projectId on confirm. */
  onDeleteProject?: (projectId: string) => void | Promise<void>;
}

export default function LandingProjects({
  projects,
  viewerUserId,
  userInitials,
  showNewProjectCard = false,
  onNewProjectClick,
  onDeleteProject,
}: LandingProjectsProps) {
  const list = projects ?? [];
  const showNewCard = showNewProjectCard && onNewProjectClick;
  if (list.length === 0 && !showNewCard) return null;

  return (
    <section className="bg-landing-bg-light py-16 px-4">
      <div className="w-full max-w-[1032px] mx-auto">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p, index) => {
            const maxStepIndex = p.blocks.length > 0
              ? Math.max(...p.blocks.map((b) => b.stepIndex))
              : -1;
            const currentStepLabel = maxStepIndex >= 0
              ? PRODUCT_STEPS[maxStepIndex]?.label
              : undefined;
            const isOwnedByViewer =
              !!viewerUserId && p.ownerId === viewerUserId;
            const onDeleteProp = isOwnedByViewer ? onDeleteProject : undefined;
            return (
              <ProjectCard
                key={p.id}
                id={p.id}
                name={p.name}
                updatedAt={p.updatedAt}
                initials={userInitials}
                owner={p.owner}
                isOwnedByViewer={isOwnedByViewer}
                index={index}
                currentStepLabel={currentStepLabel}
                onDelete={onDeleteProp}
              />
            );
          })}
          {showNewCard && (
            <NewProjectCard onClick={onNewProjectClick} />
          )}
        </div>
      </div>
    </section>
  );
}
